import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeMobileCart);
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  /** @type {{ width: string, position: string, top: string, left: string, right: string } | null} */
  #previousBodyStyle = null;

  /** @type {number | null} */
  #openingFrame = null;

  // SP cart motion: serialize close requests from rapid taps/Escape.
  #closing = false;

  #mobileCartWidth = null;

  #resizeMobileCart = () => {
    const { dialog } = this.refs;
    // Browser chrome changes height only; a width change is a genuine rotation/resize.
    if (!dialog.open || this.#mobileCartWidth === window.innerWidth) return;
    this.#mobileCartWidth = window.innerWidth;
    if (window.innerWidth <= 749) {
      dialog.style.setProperty('--sp-cart-height', `${window.visualViewport?.height || window.innerHeight}px`);
    } else {
      dialog.style.removeProperty('--sp-cart-height');
    }
  };

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    // Product/cart events can request the same drawer twice before the first animation frame runs.
    // Guard the pending frame so the saved body scroll state cannot be overwritten with fixed styles.
    if (dialog.open || this.#openingFrame !== null) return;

    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;

    // Prevent layout thrashing by separating DOM reads from DOM writes
    this.#openingFrame = requestAnimationFrame(() => {
      this.#openingFrame = null;
      // Section morphing can detach the original drawer before this queued frame runs.
      // A detached drawer must never lock the shared page body.
      if (!this.isConnected || !dialog.isConnected || dialog.open) return;
      // SP mobile cart: native modal isolation avoids a scrolling-root layout jump.
      // Freeze only this drawer's viewport height until close; other dialogs keep their lock.
      const mobileCart = this.localName === 'cart-drawer-component' &&
        matchMedia('(max-width: 749px) and (hover: none) and (pointer: coarse)').matches;
      if (mobileCart) {
        dialog.style.setProperty('--sp-cart-height', `${window.visualViewport?.height || window.innerHeight}px`);
        this.#mobileCartWidth = window.innerWidth;
        window.addEventListener('resize', this.#resizeMobileCart);
      } else if (!((this.classList.contains('account-drawer') || this.id === 'search-modal') &&
        matchMedia('(max-width: 749px) and (hover: none) and (pointer: coarse)').matches)) {
        // SP touch account/search: native modal isolation, without moving the scrolling root.
        this.#previousBodyStyle = {
          width: document.body.style.width,
          position: document.body.style.position,
          top: document.body.style.top,
          left: document.body.style.left,
          right: document.body.style.right,
        };
        document.body.style.width = '100%';
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;
        document.body.style.left = '0';
        document.body.style.right = '0';
      }

      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
      // SP fix (2026-10-18): Escape must close even when focus fell to <body> (e.g. a cart re-render replaced the
      // focused control). The native modal 'cancel' fires regardless of focus; animate the close ourselves.
      dialog.addEventListener('cancel', this.#handleCancel);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (this.#openingFrame !== null) {
      cancelAnimationFrame(this.#openingFrame);
      this.#openingFrame = null;
    }
    if (!dialog.open || this.#closing) return;
    this.#closing = true;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);
    dialog.removeEventListener('cancel', this.#handleCancel);

    // Shared header panels exit continuously even when entrance is interrupted.
    if (this.id === 'search-modal' || this.classList.contains('account-drawer') || this.localName === 'cart-drawer-component') {
      dialog.style.setProperty('--sp-panel-close-transform', getComputedStyle(dialog).transform);
      dialog.style.setProperty('--sp-panel-close-opacity', getComputedStyle(dialog, '::backdrop').opacity);
    }

    // A close tap during entrance must exit from the current position, not jump to x=0.
    if (this.localName === 'cart-drawer-component' && matchMedia('(max-width: 749px)').matches) {
      dialog.style.setProperty('--sp-cart-close-transform', getComputedStyle(dialog).transform);
      dialog.style.setProperty('--sp-cart-close-opacity', getComputedStyle(dialog, '::backdrop').opacity);
    }

    // Keep an interrupted top entrance continuous when closing the account panel.
    if (this.classList.contains('account-drawer') && matchMedia('(max-width: 749px)').matches) {
      dialog.style.setProperty('--sp-account-close-transform', getComputedStyle(dialog).transform);
      dialog.style.setProperty('--sp-account-close-opacity', getComputedStyle(dialog, '::backdrop').opacity);
    }

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    if (this.#previousBodyStyle) {
      document.body.style.width = this.#previousBodyStyle.width;
      document.body.style.position = this.#previousBodyStyle.position;
      document.body.style.top = this.#previousBodyStyle.top;
      document.body.style.left = this.#previousBodyStyle.left;
      document.body.style.right = this.#previousBodyStyle.right;
      this.#previousBodyStyle = null;
    }
    dialog.close();
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
    dialog.style.removeProperty('--sp-panel-close-transform');
    dialog.style.removeProperty('--sp-panel-close-opacity');
    dialog.style.removeProperty('--sp-cart-height');
    dialog.style.removeProperty('--sp-cart-close-transform');
    dialog.style.removeProperty('--sp-cart-close-opacity');
    dialog.style.removeProperty('--sp-account-close-transform');
    dialog.style.removeProperty('--sp-account-close-opacity');
    window.removeEventListener('resize', this.#resizeMobileCart);
    this.#mobileCartWidth = null;
    dialog.classList.remove('dialog-closing');
    this.#closing = false;

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleCancel = (event) => {
    event.preventDefault();
    this.closeDialog();
  };

  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;

        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
